import React from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import FixedStartTimeRow from './FixedStartTimeRow';
import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { Box, Typography, FormControlLabel, Button, Switch, useTheme } from '@mui/material';

import { StartTimeFixed, FixedTimesObject } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import { RiQuestionLine } from 'react-icons/ri';
import CustomTooltip from './CustomTooltip';

interface Props {
	value: StartTimeFixed;
	onChange: (value: StartTimeFixed) => void;
	disabled?: boolean;
}

const FixedStartTime = (props: Props) => {
	const { value, onChange } = props;
	const { fixedTimes, outsideOpeningHours } = value;
	const openingHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException);
	const classes = useStyles();
	const theme = useTheme();
	const { t } = useTranslation();

	const getDefaultFixedTimeWithDayType = (): FixedTimesObject => {
		return {
			dayType: openingHours.openingHours?.defaultTimes[0]?.dayType ?? 'allDays',
			time: openingHours.openingHours?.defaultTimes[0]?.openTime ?? '09:00',
		};
	};

	const handleFixedTimesObjectAdd = () => {
		const newFixedTimes = [...(fixedTimes ?? []), getDefaultFixedTimeWithDayType()];
		onChange({
			...value,
			fixedTimes: newFixedTimes,
		});
	};

	const handleFixedTimesObjectChange = (newFixedTimesObject: FixedTimesObject, index: number) => {
		const newFixedTimes = [...fixedTimes];
		newFixedTimes[index] = newFixedTimesObject;
		onChange({
			...value,
			fixedTimes: newFixedTimes,
		});
	};

	const handleFixedTimesObjectRemove = (index: number) => {
		const newFixedTimes = (fixedTimes ?? []).filter((_, i) => i !== index);
		onChange({
			...value,
			fixedTimes: newFixedTimes,
		});
	};

	const handleOutsideOpeningHoursChange = () => {
		const newOutsideOpeningHours = !outsideOpeningHours ?? true;
		onChange({
			...value,
			outsideOpeningHours: newOutsideOpeningHours,
		});
	};

	const renderFixedTimesRows = () => {
		const fixedTimesJSXArray = fixedTimes
			? fixedTimes.map((fixedTimesObj, index) => (
					<FixedStartTimeRow
						value={fixedTimesObj}
						index={index}
						onChange={(e) => handleFixedTimesObjectChange(e, index)}
						onRemove={() => handleFixedTimesObjectRemove(index)}
						key={String(fixedTimesObj.dayType + index)}
						disabled={props.disabled}
						showOpeningHoursWarning={!outsideOpeningHours}
					/>
			  ))
			: [];
		return fixedTimesJSXArray;
	};

	const renderWithinOpeningHours = () => (
		<Box mt={2}>
			<FormControlLabel
				disabled={props.disabled}
				control={
					<Switch
						checked={outsideOpeningHours}
						onChange={handleOutsideOpeningHoursChange}
						color="primary"
					/>
				}
				label={
					<Typography>
						{t(
							'product.fixedStartTimesOutsideOpeningHoursDescription',
							'Ignore store opening hours',
						)}
						<CustomTooltip
							title={t(
								'product.fixedStartTimesIgnoreOpeningHours',
								'Enable this to show all fixed start times to your customers, even if they are outside store opening hours. By default, only start times that are within opening hours will be shown.',
							)}
						>
							<Box display="inline" ml={1}>
								<RiQuestionLine color={theme.palette.text.secondary} />
							</Box>
						</CustomTooltip>
					</Typography>
				}
			/>
		</Box>
	);

	return (
		<div>
			{value.type === 'fixed' && (
				<Box className={classes.alignBox}>
					{renderFixedTimesRows()}
					<Button
						variant="outlined"
						color="primary"
						onClick={handleFixedTimesObjectAdd}
						className={classes.addRowBtn}
						disabled={props.disabled}
					>
						{`+ ${t('common:actions.addRow')}`}
					</Button>
					{renderWithinOpeningHours()}
				</Box>
			)}
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		addRowBtn: {
			marginTop: theme.spacing(3),
		},
		alertBox: {
			margin: theme.spacing(3, 0),
		},
		alignBox: {
			marginLeft: 30,
		},
	}),
);

export default FixedStartTime;
